// i18n area 'hci' (task #127): sections hci.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

export const enUS = {
  hci: {
    ceph: {
      title: 'Ceph clusters',
      createButton: 'Create Ceph cluster',
      emptyTitle: 'No managed Ceph cluster yet',
      emptyBody: 'TVirt deploys and owns Ceph on the member nodes, hyperconverged with the VM workload. Create a cluster: the first monitor and its manager are born automatically on the chosen control node — then grow it with more monitors and OSD devices.',
      pageTitle: 'Ceph clusters',
      colState: 'State',
      colMembers: 'Members',
      colSize: 'Size',
      colVersion: 'Ceph version',
      sizeCell: '{used} GiB used of {total} GiB total',
      phase: {
        'not-deployed': 'Not deployed',
        deploying: 'Deploying',
        healthy: 'Healthy',
        degraded: 'Degraded',
        error: 'Error',
        destroying: 'Destroying',
        destroyed: 'Destroyed'
      },
      health: {
        HEALTH_OK: 'Healthy (HEALTH_OK)',
        HEALTH_WARN: 'Warnings (HEALTH_WARN)',
        HEALTH_ERR: 'Error (HEALTH_ERR)',
        unknown: 'Not observed yet',
        observedAt: 'observed {time}'
      },
      create: {
        title: 'Create Ceph cluster',
        info: 'When creating a new Ceph cluster you choose the first node on which the MON and MGR roles will be installed. You must also specify which subnet to use for the Ceph traffic: the public subnet is used for the general network traffic, while the cluster subnet is used for the internal replicas between the OSDs; different subnets are not strictly required.',
        controlNode: 'Control node',
        controlNodeHint: 'The member node where the first MON + MGR are born.',
        publicSubnet: 'Public subnet',
        publicSubnetHint: 'A Subnet registered in the IPAM — Ceph public_network. Immutable after creation.',
        clusterSubnet: 'Cluster subnet',
        clusterSubnetHint: 'A Subnet registered in the IPAM — Ceph cluster_network (replication/backfill).',
        noSubnets: 'No Subnet registered — create them in the IPAM first',
        sameSubnet: 'Same subnet for public and cluster traffic',
        sameSubnetHint: 'Checked = the cluster network (replication/backfill) coincides with the public one. Unchecked = pick its own subnet AND the control node’s address inside it.',
        controlAddress: 'Control node address',
        controlAddressHint: 'An address the IPAM knows for the node inside the public subnet.',
        controlClusterAddress: 'Control node cluster address',
        controlClusterAddressHint: 'An address the IPAM knows for the node inside the cluster subnet.',
        noAddresses: 'The IPAM knows no address of this node inside the chosen subnet',
        started: 'Cluster created — the bootstrap continues as a macrotask; the phase follows on its own',
        error: 'The cluster was refused'
      },
      detail: {
        actions: 'Available actions',
        tabOverview: 'Overview',
        tabManagementNodes: 'MON & MGR',
        tabDisks: 'OSD',
        tabPools: 'Pools',
        tabObjects: 'Objects',
        tabCrush: 'CRUSH Map',
        tabCephfs: 'CephFS',
        tabAdvanced: 'Advanced',
        phase: 'Phase',
        checksTitle: 'Active health checks',
        noChecks: 'No active health checks',
        checkMuted: 'muted',
        summaryPools: 'Pools',
        summaryFilesystems: 'Filesystems',
        nodesCard: 'Nodes',
        monHint: 'An MGR runs colocated with every MON. Three MONs are the recommended quorum; a lab cluster deploys with one. OSDs and MDS live in their own tabs.',
        monHintSplit: 'MON and MGR are split on this cluster (Advanced): each role is its own membership per node. Three MONs are the recommended quorum; at least one MGR must exist.',
        noMons: 'No monitor declared yet',
        addNode: 'Add node',
        removeNode: 'Remove node',
        removeNodeFrom: 'Remove node {node} (its MON and colocated MGR)',
        roleMon: 'MON role',
        roleMgr: 'MGR role',
        roleNa: 'N/A',
        roleNotObserved: 'not observed yet',
        monInQuorum: 'in quorum',
        monOutQuorum: 'out of quorum',
        mgrActive: 'active',
        mgrStandby: 'standby',
        addRoleMon: 'Add MON role',
        removeRoleMon: 'Remove MON role',
        addRoleMgr: 'Add MGR role',
        removeRoleMgr: 'Remove MGR role',
        restartMon: 'Restart MON service',
        restartMgr: 'Restart MGR service',
        addMon: 'Add MON',
        removeMon: 'Remove MON',
        removeMonFrom: 'Remove the MON role from {node}',
        addMgr: 'Add MGR',
        removeMgr: 'Remove MGR',
        removeMgrFrom: 'Remove the MGR role from {node}',
        addMds: 'Add MDS',
        removeMds: 'Remove MDS',
        removeMdsFrom: 'Remove the MDS role from {node}',
        mdsSection: 'Metadata servers (MDS)',
        noMds: 'No MDS declared yet — a filesystem needs one first',
        mdsHint: 'The MDS membership is its own from day one: a cluster without CephFS declares none, one active plus one standby serve a filesystem well. Ceph elects actives and standbys among the declared daemons.',
        mdsActiveFor: 'active — {fs}',
        mdsNotObserved: 'not observed yet',
        lastMdsGuard: 'The last MDS cannot be removed while a filesystem exists — remove the filesystems first',
        lastMonGuard: 'The last MON cannot be removed — destroying the cluster is the only exit',
        lastMgrGuard: 'The last MGR cannot be removed — a managed cluster never runs without a manager',
        registerDevice: 'Add OSD',
        unregisterDevice: 'Unregister',
        activeDeviceGuard: 'Active OSD — it leaves through the OSD lifecycle, not the registry',
        colNode: 'Node',
        devicePhase: { registered: 'Registered', active: 'Active' },
        health: 'Health',
        state: 'State',
        fsid: 'fsid',
        fsidPending: 'absent — retry the bootstrap',
        version: 'Ceph version',
        publicNetwork: 'Public network',
        clusterNetwork: 'Cluster network',
        clusterNetworkCollapsed: '= public',
        noActions: 'No action is available in this phase',
        destroyedNote: 'Cluster destroyed — the row is removed automatically in a short while'
      },
      overview: {
        services: 'Services',
        ok: 'healthy',
        warning: 'warning',
        down: 'down',
        pgTitle: 'PG states',
        pgTotal: '{n} PGs total',
        capacityTitle: 'Capacity',
        ioTitle: 'Current I/O',
        ioReadBw: 'Read bandwidth',
        ioWriteBw: 'Write bandwidth',
        ioReadIops: 'Read IOPS',
        ioWriteIops: 'Write IOPS',
        notObserved: 'not observed yet'
      },
      osd: {
        colId: 'ID',
        colClass: 'Device class',
        colState: 'State',
        colSize: 'Size',
        colPgs: 'PGs',
        empty: 'No OSD yet — add one from a declared data disk',
        markIn: 'Mark IN',
        markOut: 'Mark OUT',
        scrub: 'Run scrub',
        deepScrub: 'Run deep scrub',
        restart: 'Restart service',
        details: 'Details',
        actionGuard: 'Only on an active OSD of a deployed cluster',
        inTitle: 'Mark OSD IN',
        inBody: 'osd.{id} returns to the data placement (ceph osd in): Ceph rebalances data back onto it.',
        outTitle: 'Mark OSD OUT',
        outBody: 'osd.{id} leaves the data placement (ceph osd out): its data rebalances away and redundancy is REDUCED while the backfill runs. Reversible at any moment with Mark IN.',
        scrubTitle: 'Schedule scrub',
        scrubBody: 'A scrub of osd.{id} is scheduled: the mon queues it and the PG states of the next sweeps tell the story.',
        deepScrubTitle: 'Schedule deep scrub',
        deepScrubBody: 'A deep scrub of osd.{id} is scheduled: the byte-verifying variant — heavier on I/O, same mechanics.',
        restartTitle: 'Restart OSD service',
        restartBody: 'osd.{id} is stopped and started again through the worker’s own supervision on its node.',
        started: 'Action started — the observation follows on its own',
        error: 'The OSD action was refused'
      },
      osdDetail: {
        title: 'OSD details',
        tabGeneral: 'General',
        tabNetwork: 'Network',
        tabDevices: 'Devices',
        version: 'Version',
        hostname: 'Hostname',
        dataPath: 'OSD data path',
        objectStore: 'OSD object store',
        encrypted: 'Encrypted',
        yes: 'Yes',
        no: 'No',
        memory: 'Memory usage (PSS)',
        pid: 'Process ID (PID)',
        front: 'Front Address (Client & Monitor)',
        hbFront: 'Heartbeat Front Address',
        back: 'Back Address (OSD)',
        hbBack: 'Heartbeat Back Address',
        colDevice: 'Device',
        colType: 'Type',
        colPhysical: 'Physical Device',
        colSize: 'Size',
        volumeTitle: 'Volume details',
        creationTime: 'Creation time',
        lvName: 'LV Name',
        lvPath: 'LV Path',
        lvUuid: 'LV UUID',
        vgName: 'VG Name',
        noDevices: 'No device observed for this OSD'
      },
      restart: {
        title: 'Restart service',
        monBody: 'The MON daemon on {node} is stopped and started again through the worker’s own supervision.',
        mgrBody: 'The MGR daemon on {node} is stopped and started again through the worker’s own supervision.',
        started: 'Restart started — the observation follows on its own',
        error: 'The restart was refused'
      },
      encryptionMode: {
        alwaysOn: 'Always enabled (dmcrypt)',
        alwaysOff: 'Always disabled',
        perOsd: 'Chosen per OSD',
        section: 'OSD encryption at rest',
        title: 'Change OSD encryption mode',
        hint: 'How NEW OSDs are prepared. Existing OSDs keep their per-disk state: to change one, remove it and recreate it.',
        body: 'The mode becomes "{mode}" and applies to NEW OSDs ONLY: every existing OSD keeps its current encryption state.',
        apply: 'Change mode',
        error: 'The mode change was refused'
      },
      pin: {
        address: 'Public address (optional pin)',
        clusterAddress: 'Cluster address (optional pin)',
        autoPlaceholder: 'automatic inside {cidr}',
        hint: 'Per-daemon addressing: empty = Ceph auto-binds inside the declared network; set = this daemon instance is pinned to the address in its conf section.'
      },
      addMon: {
        title: 'Add node',
        node: 'Node',
        address: 'Public address',
        addressHint: 'The node’s addresses the IPAM knows inside the public subnet — automatic when an existing declaration covers the node.',
        clusterAddress: 'Cluster address',
        clusterAddressHint: 'Required: the cluster subnet is separate and the node is not declared on it yet.',
        noAddresses: 'The IPAM knows no address of this node inside the subnet',
        error: 'The mon addition was refused'
      },
      removeMon: {
        title: 'Remove MON',
        body: 'The MON and its colocated MGR leave node {node}. On a deployed cluster the daemons are stopped and removed from the quorum.',
        error: 'The mon removal was refused'
      },
      addMgr: {
        title: 'Add MGR',
        node: 'Node',
        hint: 'Advanced (split MON/MGR): a standalone manager is born on the chosen member node by a node-affine task. Ceph’s own active/standby election picks the active MGR.',
        error: 'The MGR addition was refused'
      },
      removeMgr: {
        title: 'Remove MGR',
        body: 'The standalone MGR leaves node {node}. On a deployed cluster the daemon is stopped and its state removed; the node’s MON (if any) is untouched.',
        error: 'The MGR removal was refused'
      },
      addMds: {
        title: 'Add MDS',
        node: 'Node',
        hint: 'The metadata server of CephFS: born on the chosen member node by a node-affine task, worker-supervised like MON and MGR. Ceph elects actives and standbys on its own — one active plus one standby serve a filesystem well.',
        error: 'The MDS addition was refused'
      },
      removeMds: {
        title: 'Remove MDS',
        body: 'The MDS leaves node {node}. On a deployed cluster the daemon is stopped and its state removed; the API refuses the last MDS while a filesystem still needs it.',
        error: 'The MDS removal was refused'
      },
      split: {
        title: 'Split MON/MGR',
        label: 'Allow MON and MGR on disjoint nodes',
        hint: 'Off (default) = the ratified standard: an MGR runs colocated with every MON, one membership. On = the MGR membership becomes its own, managed with the Add/Remove MGR actions in the Management nodes tab.',
        enable: 'Enable',
        disable: 'Disable',
        enableBody: 'The MGR membership becomes independent from the MONs, initialized from the current colocation. The standalone Add/Remove MGR actions arm in the Management nodes tab. This relaxes the platform default — keep it only if you know why you need it.',
        disableBody: 'Back to the standard colocation: an MGR with every MON, one membership. Legal only while the MGR membership already coincides with the MONs — align it with Add/Remove MGR first, or the API will refuse.',
        error: 'The change was refused'
      },
      rawEditor: {
        title: 'Raw CRUSH editor',
        label: 'Show the raw crushmap editor in the CRUSH tab',
        hint: 'Off (default) = the guided rule surface is the standard tool. On = the CRUSH tab also shows the decompiled crushmap as editable text. Saving always requires sudo, the cluster name as confirmation, and passes crushtool compile + test BEFORE reaching the cluster.'
      },
      osdFlags: {
        section: 'Global OSD flags',
        title: 'OSD flag override',
        notObserved: 'not observed yet',
        standardFlowsHint: 'The standard platform flows manage these flags on their own in the normal cases (node maintenance sets a scoped noout, device replacement rides the OSD lifecycle). This section is the manual override for the operator who knows what they are doing.',
        notDeployedGuard: 'OSD flags exist only on a deployed cluster',
        set: 'Set flag',
        unset: 'Clear flag',
        setBody: 'The cluster-wide flag {flag} will be SET on the osdmap. It stays until explicitly cleared — forgotten flags degrade the cluster’s self-healing.',
        unsetBody: 'The cluster-wide flag {flag} will be CLEARED from the osdmap.',
        error: 'The flag change was refused',
        flag: {
          noout: 'No OSD is marked out of the data distribution — no rebalancing on daemon loss',
          norebalance: 'Data rebalancing is suspended',
          nobackfill: 'Backfill is suspended',
          norecover: 'Recovery is suspended',
          pause: 'ALL client I/O is paused (reads and writes)',
          noin: 'Booting OSDs are not marked in automatically',
          nodown: 'Unreachable OSDs are not marked down — masks real failures'
        }
      },
      pools: {
        section: 'Pools',
        create: 'Create pool',
        createGuard: 'Pools exist only on a deployed cluster',
        notObserved: 'No pool observation yet — the mon sweep reports here',
        empty: 'No pool on the cluster yet',
        colName: 'Name',
        colReplication: 'Size / min size',
        colPgNum: 'PGs',
        colApplication: 'Application',
        colRule: 'CRUSH rule',
        colUsage: 'Usage',
        observedAtLabel: 'Observed at',
        objects: '{n} objects',
        inUseBy: 'used by {name}',
        internalGuard: 'Ceph-internal pool — the platform never deletes it',
        delete: 'Delete pool',
        deleteGuard: 'Consumed by storage {name} — delete the storage first',
        hint: 'Replicated pools only (erasure coding is out of scope v1). Replication lives in Ceph: this table is the observed truth from the mon sweep.',
        createTitle: 'Create pool',
        createHint: 'The pool is born on the cluster with the requested replication and the rbd application enabled — a task on a MON node does the work. Turn on "Propose as storage" to consume it right away in the same gesture.',
        name: 'Pool name',
        nameHint: 'DNS-safe (lowercase letters, digits, hyphens).',
        size: 'Replicas (size)',
        sizeHint: 'Copies of every object. Default 3.',
        minSize: 'Min replicas (min_size)',
        minSizeHint: 'Minimum copies to serve I/O — follows size − 1 until you edit it.',
        createError: 'The pool creation was refused',
        deleteTitle: 'Delete pool',
        deleteBody: 'The pool {pool} and EVERY object in it are destroyed irreversibly. Type the pool name to confirm.',
        confirmLabel: 'Pool name',
        confirmHint: "Type the pool's name to confirm",
        deleteError: 'The pool deletion was refused',
        detailTitle: 'Pool details',
        autoscaler: 'PG Autoscaler',
        autoscalerOn: 'Enabled',
        autoscalerOff: 'Disabled',
        autoscalerWarn: 'Notify',
        pgNumLabel: 'PG count',
        pgNumCaption: 'Observed: the autoscaler may change it on its own — this field writes a desire.',
        storage: 'Associated TVirt storage',
        addAsStorage: 'Add as TVirt storage',
        ruleSudoHint: 'Changing the CRUSH rule requires the operator password.',
        updateError: 'The pool update was refused',
        updated: 'Pool update started — the observation follows on its own'
      },
      crush: {
        section: 'CRUSH rules',
        notObserved: 'No CRUSH observation yet — the mon sweep reports here',
        empty: 'No rule observed on the cluster',
        hint: 'A rule decides WHERE the copies of every object land — its failure domain is the unit that can fail without losing data. Pools point at a rule; the builtin replicated_rule (one replica per server) is the birth default.',
        create: 'Create rule',
        createGuard: 'CRUSH rules exist only on a deployed cluster',
        createTitle: 'Create CRUSH rule',
        createHint: 'One rule = one failure-domain choice. The rule is born replicated on the default root — a task on a MON node does the work; associate pools from the Pools tab afterwards.',
        createError: 'The rule creation was refused',
        name: 'Rule name',
        nameHint: 'DNS-safe (lowercase letters, digits, hyphens).',
        domain: 'Failure domain',
        domainChoice: {
          osd: 'Every replica on a different OSD',
          host: 'Every replica on a different server',
          rack: 'Every replica in a different rack'
        },
        domainCustom: 'Custom (hand-written steps)',
        domainOther: 'Every replica in a different {type}',
        rackGuardHint: 'No rack bucket exists in the observed hierarchy: the rule can be created anyway, but no pool can be pointed at it until a rack is declared in the member servers’ placement (node settings) — the realignment task builds the bucket and the association opens by itself.',
        treeTitle: 'Observed hierarchy',
        treeNotObserved: 'No hierarchy observation yet — the mon sweep reports here',
        treeOsds: '{n} OSD',
        treeHint: 'The CRUSH map as the cluster sees it: root → declared levels → servers (hosts) → OSDs. The topology follows each server’s declared placement (node settings) — a placement change realigns it through a task.',
        unplacedWarning: 'Members without a declared placement: {nodes} — their host buckets stay directly under the root, outside the declared topology. Declare their position in the node settings (a warning, never a block).',
        colName: 'Name',
        colDomain: 'Failure domain',
        colPools: 'Pools',
        builtinChip: 'builtin',
        builtinGuard: 'The cluster’s builtin default rule — the platform never deletes it',
        detailAction: 'Rule detail',
        detailType: 'Type',
        backToRules: 'Rules',
        ruleNotObserved: 'The observed rule list does not name this rule',
        noPools: 'No pool rides this rule',
        delete: 'Delete rule',
        deleteTitle: 'Delete CRUSH rule',
        deleteBody: 'The rule {rule} is removed from the cluster’s crushmap. No pool may ride it. Type the rule name to confirm.',
        deleteGuard: 'Ridden by pool {name} — repoint the pool first',
        confirmLabel: 'Rule name',
        confirmHint: "Type the rule's name to confirm",
        deleteError: 'The rule deletion was refused',
        changeRule: 'Change CRUSH rule',
        changeRuleTitle: 'Change CRUSH rule',
        changeRuleHint: 'The pool starts placing its objects by the chosen rule — Ceph moves the existing data to comply (a rebalance follows on a full pool).',
        changeRuleError: 'The rule change was refused',
        rule: 'CRUSH rule',
        rawTitle: 'Raw crushmap (decompiled)',
        rawWarning: 'DANGER: this text is the WHOLE placement policy of the cluster. A wrong map can break the placement of every pool at once. Saving compiles and test-runs the map with crushtool BEFORE injecting it — but a valid map can still be a bad idea.',
        rawReload: 'Reload',
        rawLoading: 'Query in flight — a read-only task is decompiling the crushmap…',
        rawSave: 'Save crushmap',
        rawSaveTitle: 'Inject crushmap',
        rawSaveBody: 'The edited text replaces the crushmap of the WHOLE cluster after crushtool compile + test. Type the cluster name to confirm.',
        rawConfirmLabel: 'Cluster name',
        rawConfirmHint: 'Type {name} to confirm',
        rawError: 'The crushmap operation failed',
        rawHint: 'The text is the crushtool -d form. Compile errors and test failures come back verbatim from crushtool — the map never reaches the cluster if either gate fails.'
      },
      objects: {
        poolsSection: 'Object explorer',
        poolsHint: 'Pick a pool to list its RBD images. Every deep read runs as a read-only task on a MON node — it never blocks (nor is blocked by) real operations.',
        deployedGuard: 'Object queries answer only on a deployed cluster',
        browse: 'Browse objects',
        detailAction: 'Image detail',
        backToPools: 'Pools',
        backToImages: 'Images',
        refresh: 'Refresh',
        loading: 'Query in flight — a read-only task is asking the cluster…',
        error: 'The query failed',
        retry: 'Retry',
        notRbd: 'Pool application "{app}" — it holds no RBD images, nothing to explore here',
        noImages: 'No RBD images in this pool',
        colImage: 'Image',
        colSize: 'Size',
        colObjects: 'Objects',
        colSnapshots: 'Snapshots',
        imagesHint: 'Disks on a consuming rbd storage are images named by their disk id. Object counts are provisioned (size / object size) — the real usage lives in the image detail.',
        infoTitle: 'Image',
        infoSize: 'Virtual size',
        infoObjects: 'Objects (provisioned)',
        infoObjectSize: 'Object size',
        infoPrefix: 'Object prefix',
        infoFormat: 'Format',
        infoFeatures: 'Features',
        usageTitle: 'Real usage (rbd du)',
        usageOf: 'of {provisioned} GiB provisioned',
        usageHint: 'Thin by nature: only written objects occupy bytes.',
        watchersTitle: 'Watchers',
        noWatchers: 'No watcher — nothing has this image open right now',
        snapshotsTitle: 'RBD snapshots',
        noSnapshots: 'No RBD snapshot on this image',
        colSnapshot: 'Snapshot',
        colProtected: 'Protected',
        colTimestamp: 'Created',
        protected: 'protected',
        mappingTitle: 'Physical placement (OSD set)',
        mappingSampled: 'Sampled on {sampled} of {total} objects — an honest summary: for large images the set converges to EVERY OSD of the pool (CRUSH distributes objects across the whole pool).',
        mappingComplete: 'Every object mapped ({total} of {total}).',
        objectQueryTitle: 'Map a single object',
        objectName: 'Object name',
        objectHint: 'RADOS object names are <prefix>.<16-hex index> — ceph osd map answers its PG and OSD sets.',
        mapObject: 'Map',
        primary: 'primary: osd.{osd}'
      },
      registerDevice: {
        title: 'Register OSD device',
        hint: 'On a live cluster the registration also PREPARES the device: it becomes a BlueStore OSD through an automatic node-affine task (never empty, ratified 2026-07-24). A device belongs to at most one Ceph cluster fleet-wide.',
        disk: 'Data disk',
        diskHint: 'The node\'s declared data disks — name and serial pin',
        noDeclared: 'No data disks declared on this node — mark them in the node\'s local settings first',
        serialHint: 'The device serial — the identity pin',
        encrypted: 'Encrypt this OSD (dmcrypt)',
        encryptedHint: 'The per-OSD choice (the cluster mode is "chosen per OSD"): it freezes on this disk at registration — a later mode change never rewrites it; the encrypt/decrypt row action does.',
        error: 'The registration was refused'
      },
      unregisterDevice: {
        title: 'Unregister device',
        body: 'Registry only: {name} stops being an OSD candidate of this cluster. Its bytes are not touched.',
        error: 'The unregistration was refused'
      },
      deploy: {
        action: 'Retry deploy',
        title: 'Retry the bootstrap',
        body: 'The birth bootstrap runs again from the current configuration: mons bootstrap, every registered device is prepared and activated as a BlueStore OSD. The operation runs as a macrotask — the phase follows on its own.',
        started: 'Bootstrap started — it continues as a macrotask; the phase follows on its own',
        error: 'The bootstrap retry was refused'
      },
      propose: {
        action: 'Propose as storage',
        title: 'Propose as storage',
        hintPool: 'The existing pool {name} becomes a TVirt storage: a pool-capped cephx identity lands in the keystore and the ceph-rbd storage is born ready. The proposal consumes the pool — it never creates one.',
        hintFs: 'The existing filesystem {name} becomes a TVirt storage: a filesystem-capped cephx identity lands in the keystore and the cephfs storage is born ready and mounted. The proposal consumes the filesystem — it never creates one.',
        name: 'Storage name',
        nameHint: 'DNS-1123, unique in the scope — the cephx identity is named after it.',
        path: 'Path inside the filesystem',
        pathHint: 'Absolute path inside the filesystem the storage mounts — / if in doubt.',
        alsoPropose: 'Propose as storage right away',
        alsoProposePoolHint: 'After the creation the same gesture proposes the newborn pool as a ceph-rbd storage — two explicit calls, one dialog.',
        alsoProposeFsHint: 'After the creation the same gesture proposes the newborn filesystem as a cephfs storage — two explicit calls, one dialog.',
        guard: 'Propose as storage — only on a healthy cluster',
        started: 'Proposal accepted — the storage is being materialized; find it under Storage',
        submit: 'Create storage',
        error: 'The proposal was refused'
      },
      fs: {
        section: 'Filesystems',
        create: 'Create filesystem',
        createGuard: 'Filesystems exist only on a deployed cluster',
        mdsFirstGuard: 'Add an MDS first — a filesystem without a metadata server is born unserviceable',
        notObserved: 'No filesystem observation yet — the MON sweep reports here',
        empty: 'No filesystem on the cluster yet',
        colName: 'Name',
        colPools: 'Pools',
        colMds: 'MDS',
        noActiveMds: 'no active MDS',
        delete: 'Remove filesystem',
        deleteGuard: 'Consumed by storage {name} — delete the storage first',
        hint: 'A filesystem owns its data and metadata pools (cephfs.<name>.data / .meta): they are born with it and die with it — removeFs, never a bare pool delete. This table is the observed truth from the MON sweep.',
        createTitle: 'Create filesystem',
        createHint: 'The filesystem is born on the cluster with its two pools (application cephfs) and served by the declared MDS — a task on a MON node does the work. Turn on "Propose as storage" to consume it right away in the same gesture.',
        name: 'Filesystem name',
        nameHint: 'DNS-safe (lowercase letters, digits, hyphens).',
        sizeHint: 'Replicas of BOTH newborn pools. Default 3.',
        createError: 'The filesystem creation was refused',
        deleteTitle: 'Remove filesystem',
        deleteBody: 'The filesystem {fs} and EVERY file in it are destroyed irreversibly — its data and metadata pools die with it. Type the filesystem name to confirm.',
        confirmLabel: 'Filesystem name',
        confirmHint: "Type the filesystem's name to confirm",
        deleteError: 'The filesystem removal was refused'
      },
      destroy: {
        action: 'Destroy',
        retryAction: 'Retry the teardown',
        retryGuard: 'The teardown is running — the retry applies to a failed one (the lease refuses it otherwise)',
        title: 'Destroy the cluster',
        body: 'The deployed fabric is torn down: every daemon of this cluster stops, its system state and keystore keys are released. Once the teardown succeeds the cluster is removed automatically.',
        confirmLabel: "Cluster name",
        confirmHint: "Type the cluster's name to confirm",
        wipe: 'Wipe the registered devices',
        wipeHint: 'Checked: the signatures of every registered device are wiped — the data on them is destroyed. Unchecked: the bytes stay intact and the devices return to registered.',
        guard: 'Destroy — nothing deployed',
        started: 'Destroy started — it continues as a macrotask; the phase follows on its own',
        error: 'The destroy was refused'
      },
      delete: {
        guard: 'Delete — only a not-deployed configuration (destroy first)',
        title: 'Delete {name}?',
        body: 'The configuration and its device registry are removed. Legal only while nothing is deployed.',
        error: 'The deletion was refused'
      }
    }
  }
}

export const itIT: typeof enUS = {
  hci: {
    ceph: {
      title: 'Cluster Ceph',
      createButton: 'Crea cluster Ceph',
      emptyTitle: 'Nessun cluster Ceph gestito',
      emptyBody: 'TVirt distribuisce e possiede Ceph sui nodi membri, iperconvergente col carico VM. Crea un cluster: il primo monitor e il suo manager nascono automaticamente sul nodo di controllo scelto — poi crescilo con altri monitor e dispositivi OSD.',
      pageTitle: 'Cluster Ceph',
      colState: 'Stato',
      colMembers: 'Membri',
      colSize: 'Dimensione',
      colVersion: 'Versione Ceph',
      sizeCell: '{used} GiB usati su {total} GiB totali',
      phase: {
        'not-deployed': 'Non deployato',
        deploying: 'Deploy in corso',
        healthy: 'In salute',
        degraded: 'Degradato',
        error: 'Errore',
        destroying: 'In distruzione',
        destroyed: 'Distrutto'
      },
      health: {
        HEALTH_OK: 'In salute (HEALTH_OK)',
        HEALTH_WARN: 'Con avvisi (HEALTH_WARN)',
        HEALTH_ERR: 'In errore (HEALTH_ERR)',
        unknown: 'Non ancora osservata',
        observedAt: 'osservata {time}'
      },
      create: {
        title: 'Crea cluster Ceph',
        info: 'Quando si crea un nuovo cluster Ceph va scelto il primo nodo sul quale verranno installati i ruoli MON ed MGR. Va inoltre specificata quale subnet usare per far girare il traffico Ceph: la public subnet viene usata per il traffico di rete generale, mentre la cluster subnet viene usata per le repliche interne tra gli OSD; non c’è bisogno per forza di subnet diverse.',
        controlNode: 'Nodo di controllo',
        controlNodeHint: 'Il nodo membro dove nascono il primo MON + MGR.',
        publicSubnet: 'Subnet public',
        publicSubnetHint: 'Una Subnet registrata nell’IPAM — la public_network di Ceph. Immutabile dopo la creazione.',
        clusterSubnet: 'Subnet cluster',
        clusterSubnetHint: 'Una Subnet registrata nell’IPAM — la cluster_network di Ceph (replica/backfill).',
        noSubnets: 'Nessuna Subnet registrata — creale prima nell’IPAM',
        sameSubnet: 'Stessa subnet per il traffico public e cluster',
        sameSubnetHint: 'Spuntata = la rete cluster (replica/backfill) coincide con la public. Non spuntata = scegli la sua subnet E l’indirizzo del nodo di controllo al suo interno.',
        controlAddress: 'Indirizzo del nodo di controllo',
        controlAddressHint: 'Un indirizzo che l’IPAM conosce per il nodo dentro la subnet public.',
        controlClusterAddress: 'Indirizzo cluster del nodo di controllo',
        controlClusterAddressHint: 'Un indirizzo che l’IPAM conosce per il nodo dentro la subnet cluster.',
        noAddresses: 'L’IPAM non conosce indirizzi di questo nodo dentro la subnet scelta',
        started: 'Cluster creato — il bootstrap prosegue come macrotask; la fase si aggiorna da sola',
        error: 'Il cluster è stato rifiutato'
      },
      detail: {
        actions: 'Azioni disponibili',
        tabOverview: 'Panoramica',
        tabManagementNodes: 'MON & MGR',
        tabDisks: 'OSD',
        tabPools: 'Pool',
        tabObjects: 'Oggetti',
        tabCrush: 'CRUSH Map',
        tabCephfs: 'CephFS',
        tabAdvanced: 'Avanzate',
        phase: 'Fase',
        checksTitle: 'Check di salute attivi',
        noChecks: 'Nessun check di salute attivo',
        checkMuted: 'silenziato',
        summaryPools: 'Pool',
        summaryFilesystems: 'Filesystem',
        nodesCard: 'Nodi',
        monHint: 'Un MGR corre colocato con ogni MON. Tre MON sono il quorum raccomandato; un cluster di laboratorio parte con uno. OSD e MDS vivono nei loro tab.',
        monHintSplit: 'MON e MGR sono disgiunti su questo cluster (Avanzate): ogni ruolo è una membership propria per nodo. Tre MON sono il quorum raccomandato; almeno un MGR deve esistere.',
        noMons: 'Nessun monitor dichiarato',
        addNode: 'Aggiungi nodo',
        removeNode: 'Rimuovi nodo',
        removeNodeFrom: 'Rimuovi il nodo {node} (il suo MON e il MGR colocato)',
        roleMon: 'Ruolo MON',
        roleMgr: 'Ruolo MGR',
        roleNa: 'N/A',
        roleNotObserved: 'non ancora osservato',
        monInQuorum: 'in quorum',
        monOutQuorum: 'fuori quorum',
        mgrActive: 'attivo',
        mgrStandby: 'standby',
        addRoleMon: 'Aggiungi ruolo MON',
        removeRoleMon: 'Rimuovi ruolo MON',
        addRoleMgr: 'Aggiungi ruolo MGR',
        removeRoleMgr: 'Rimuovi ruolo MGR',
        restartMon: 'Riavvia servizio MON',
        restartMgr: 'Riavvia servizio MGR',
        addMon: 'Aggiungi MON',
        removeMon: 'Rimuovi MON',
        removeMonFrom: 'Rimuovi il ruolo MON da {node}',
        addMgr: 'Aggiungi MGR',
        removeMgr: 'Rimuovi MGR',
        removeMgrFrom: 'Rimuovi il ruolo MGR da {node}',
        addMds: 'Aggiungi MDS',
        removeMds: 'Rimuovi MDS',
        removeMdsFrom: 'Rimuovi il ruolo MDS da {node}',
        mdsSection: 'Metadata server (MDS)',
        noMds: 'Nessun MDS dichiarato — un filesystem ne richiede prima uno',
        mdsHint: 'La membership MDS è propria dal primo giorno: un cluster senza CephFS non ne dichiara, un attivo più uno standby servono bene un filesystem. Ceph elegge attivi e standby tra i daemon dichiarati.',
        mdsActiveFor: 'attivo — {fs}',
        mdsNotObserved: 'non ancora osservato',
        lastMdsGuard: "L'ultimo MDS non si può rimuovere finché esiste un filesystem — rimuovi prima i filesystem",
        lastMonGuard: "L'ultimo MON non si può rimuovere — distruggere il cluster è l'unica uscita",
        lastMgrGuard: "L'ultimo MGR non si può rimuovere — un cluster gestito non corre mai senza manager",
        registerDevice: 'Aggiungi OSD',
        unregisterDevice: 'Deregistra',
        activeDeviceGuard: 'OSD attivo — esce attraverso il ciclo di vita OSD, non dal registro',
        colNode: 'Nodo',
        devicePhase: { registered: 'Registrato', active: 'Attivo' },
        health: 'Salute',
        state: 'Stato',
        fsid: 'fsid',
        fsidPending: 'assente — riprova il bootstrap',
        version: 'Versione Ceph',
        publicNetwork: 'Rete public',
        clusterNetwork: 'Rete cluster',
        clusterNetworkCollapsed: '= public',
        noActions: 'Nessuna azione disponibile in questa fase',
        destroyedNote: 'Cluster distrutto — la riga viene rimossa automaticamente a breve'
      },
      overview: {
        services: 'Servizi',
        ok: 'in salute',
        warning: 'warning',
        down: 'giù',
        pgTitle: 'Stati dei PG',
        pgTotal: '{n} PG totali',
        capacityTitle: 'Spazio',
        ioTitle: 'I/O attuale',
        ioReadBw: 'Banda in lettura',
        ioWriteBw: 'Banda in scrittura',
        ioReadIops: 'IOPS in lettura',
        ioWriteIops: 'IOPS in scrittura',
        notObserved: 'non ancora osservato'
      },
      osd: {
        colId: 'ID',
        colClass: 'Device class',
        colState: 'Stato',
        colSize: 'Dimensione',
        colPgs: 'PG',
        empty: 'Nessun OSD — aggiungine uno da un disco dati dichiarato',
        markIn: 'Marca IN',
        markOut: 'Marca OUT',
        scrub: 'Effettua scrub',
        deepScrub: 'Effettua scrub profondo',
        restart: 'Riavvia servizio',
        details: 'Dettagli',
        actionGuard: 'Solo su un OSD attivo di un cluster deployato',
        inTitle: 'Marca OSD IN',
        inBody: 'osd.{id} torna nella distribuzione dei dati (ceph osd in): Ceph ribilancia i dati su di lui.',
        outTitle: 'Marca OSD OUT',
        outBody: 'osd.{id} esce dalla distribuzione dei dati (ceph osd out): i suoi dati si ribilanciano altrove e la ridondanza è RIDOTTA finché il backfill corre. Reversibile in ogni momento con Marca IN.',
        scrubTitle: 'Pianifica scrub',
        scrubBody: 'Viene pianificato uno scrub di osd.{id}: il mon lo accoda e gli stati dei PG degli sweep successivi raccontano l’esito.',
        deepScrubTitle: 'Pianifica scrub profondo',
        deepScrubBody: 'Viene pianificato uno scrub profondo di osd.{id}: la variante che verifica i byte — più pesante sull’I/O, stessa meccanica.',
        restartTitle: 'Riavvia servizio OSD',
        restartBody: 'osd.{id} viene fermato e riavviato attraverso la supervisione del worker sul suo nodo.',
        started: 'Azione avviata — l’osservazione si aggiorna da sola',
        error: 'L’azione OSD è stata rifiutata'
      },
      osdDetail: {
        title: 'Dettagli OSD',
        tabGeneral: 'Generale',
        tabNetwork: 'Rete',
        tabDevices: 'Dispositivi',
        version: 'Versione',
        hostname: 'Hostname',
        dataPath: 'Percorso dati OSD',
        objectStore: 'Object store OSD',
        encrypted: 'Crittografato',
        yes: 'Sì',
        no: 'No',
        memory: 'Uso memoria (PSS)',
        pid: 'Process ID (PID)',
        front: 'Indirizzo front (client & monitor)',
        hbFront: 'Indirizzo heartbeat front',
        back: 'Indirizzo back (OSD)',
        hbBack: 'Indirizzo heartbeat back',
        colDevice: 'Device',
        colType: 'Tipo',
        colPhysical: 'Device fisico',
        colSize: 'Dimensione',
        volumeTitle: 'Dettagli volume',
        creationTime: 'Data di creazione',
        lvName: 'LV Name',
        lvPath: 'LV Path',
        lvUuid: 'LV UUID',
        vgName: 'VG Name',
        noDevices: 'Nessun device osservato per questo OSD'
      },
      restart: {
        title: 'Riavvia servizio',
        monBody: 'Il daemon MON su {node} viene fermato e riavviato attraverso la supervisione del worker.',
        mgrBody: 'Il daemon MGR su {node} viene fermato e riavviato attraverso la supervisione del worker.',
        started: 'Riavvio avviato — l’osservazione si aggiorna da sola',
        error: 'Il riavvio è stato rifiutato'
      },
      encryptionMode: {
        alwaysOn: 'Sempre abilitata (dmcrypt)',
        alwaysOff: 'Sempre disabilitata',
        perOsd: 'Scelta per ogni OSD',
        section: 'Crittografia OSD at rest',
        title: 'Cambia modalità di crittografia OSD',
        hint: 'Come vengono preparati i NUOVI OSD. Gli OSD esistenti tengono il loro stato per-disco: per cambiarlo si rimuove e si ricrea l’OSD.',
        body: 'La modalità diventa "{mode}" e vale SOLO per i NUOVI OSD: ogni OSD esistente tiene il suo stato di crittografia attuale.',
        apply: 'Cambia modalità',
        error: 'Il cambio di modalità è stato rifiutato'
      },
      pin: {
        address: 'Indirizzo public (pin opzionale)',
        clusterAddress: 'Indirizzo cluster (pin opzionale)',
        autoPlaceholder: 'automatico nella subnet {cidr}',
        hint: 'Indirizzamento per-demone: vuoto = Ceph si aggancia da solo dentro la rete dichiarata; valorizzato = questa istanza viene pinnata all’indirizzo nella sua sezione di conf.'
      },
      addMon: {
        title: 'Aggiungi nodo',
        node: 'Nodo',
        address: 'Indirizzo public',
        addressHint: 'Gli indirizzi del nodo che l’IPAM conosce dentro la subnet public — automatico quando una dichiarazione esistente copre il nodo.',
        clusterAddress: 'Indirizzo cluster',
        clusterAddressHint: 'Obbligatorio: la subnet cluster è separata e il nodo non vi è ancora dichiarato.',
        noAddresses: 'L’IPAM non conosce indirizzi di questo nodo dentro la subnet',
        error: "L'aggiunta del mon è stata rifiutata"
      },
      removeMon: {
        title: 'Rimuovi MON',
        body: 'Il MON e il suo MGR colocato lasciano il nodo {node}. Su un cluster deployato i daemon vengono fermati e rimossi dal quorum.',
        error: 'La rimozione del MON è stata rifiutata'
      },
      addMgr: {
        title: 'Aggiungi MGR',
        node: 'Nodo',
        hint: 'Avanzate (MON/MGR disgiunti): un manager standalone nasce sul nodo membro scelto con un task affine al nodo. L’elezione active/standby di Ceph sceglie da sola il MGR attivo.',
        error: "L'aggiunta del MGR è stata rifiutata"
      },
      removeMgr: {
        title: 'Rimuovi MGR',
        body: 'Il MGR standalone lascia il nodo {node}. Su un cluster deployato il daemon viene fermato e il suo stato rimosso; il MON del nodo (se c’è) non viene toccato.',
        error: 'La rimozione del MGR è stata rifiutata'
      },
      addMds: {
        title: 'Aggiungi MDS',
        node: 'Nodo',
        hint: 'Il metadata server di CephFS: nasce sul nodo membro scelto con un task affine al nodo, supervisionato dal worker come MON e MGR. Ceph elegge da solo attivi e standby — un attivo più uno standby servono bene un filesystem.',
        error: "L'aggiunta dell'MDS è stata rifiutata"
      },
      removeMds: {
        title: 'Rimuovi MDS',
        body: "L'MDS lascia il nodo {node}. Su un cluster deployato il daemon viene fermato e il suo stato rimosso; la API rifiuta l'ultimo MDS finché un filesystem ne ha bisogno.",
        error: "La rimozione dell'MDS è stata rifiutata"
      },
      split: {
        title: 'MON/MGR disgiunti',
        label: 'Permetti MON e MGR su nodi disgiunti',
        hint: 'Spento (default) = lo standard ratificato: un MGR corre colocato con ogni MON, una sola membership. Acceso = la membership dei MGR diventa propria, gestita con le azioni Aggiungi/Rimuovi MGR nel tab Nodi di gestione.',
        enable: 'Attiva',
        disable: 'Disattiva',
        enableBody: 'La membership dei MGR diventa indipendente dai MON, inizializzata dalla colocazione attuale. Le azioni Aggiungi/Rimuovi MGR si armano nel tab Nodi di gestione. Questo rilassa il default di piattaforma — tienilo solo se sai perché ti serve.',
        disableBody: 'Ritorno alla colocazione standard: un MGR con ogni MON, una sola membership. Legale solo se la membership dei MGR coincide già con i MON — allineala prima con Aggiungi/Rimuovi MGR, o la API rifiuterà.',
        error: 'La modifica è stata rifiutata'
      },
      rawEditor: {
        title: 'Editor CRUSH grezzo',
        label: 'Mostra l\'editor grezzo della crushmap nel tab CRUSH',
        hint: 'Spento (default) = la superficie guidata delle regole è lo strumento standard. Acceso = il tab CRUSH mostra anche la crushmap decompilata come testo editabile. Il salvataggio richiede sempre sudo, il nome del cluster come conferma, e passa da compile + test di crushtool PRIMA di raggiungere il cluster.'
      },
      osdFlags: {
        section: 'Flag OSD globali',
        title: 'Override flag OSD',
        notObserved: 'non ancora osservati',
        standardFlowsHint: 'I flussi standard della piattaforma gestiscono questi flag da soli nelle casistiche normali (la manutenzione di un nodo imposta un noout mirato, la sostituzione disco passa dal ciclo di vita OSD). Questa sezione è l’override manuale per l’operatore che sa cosa sta facendo.',
        notDeployedGuard: 'I flag OSD esistono solo su un cluster deployato',
        set: 'Imposta flag',
        unset: 'Rimuovi flag',
        setBody: 'Il flag globale {flag} verrà IMPOSTATO sull’osdmap. Resta finché non viene rimosso esplicitamente — un flag dimenticato degrada l’auto-riparazione del cluster.',
        unsetBody: 'Il flag globale {flag} verrà RIMOSSO dall’osdmap.',
        error: 'La modifica del flag è stata rifiutata',
        flag: {
          noout: 'Nessun OSD viene marcato out dalla distribuzione dei dati — niente ribilanciamento alla perdita di un daemon',
          norebalance: 'Il ribilanciamento dei dati è sospeso',
          nobackfill: 'Il backfill è sospeso',
          norecover: 'Il recovery è sospeso',
          pause: 'TUTTO l’I/O dei client è in pausa (letture e scritture)',
          noin: 'Gli OSD che si avviano non vengono marcati in automaticamente',
          nodown: 'Gli OSD irraggiungibili non vengono marcati down — maschera guasti reali'
        }
      },
      pools: {
        section: 'Pool',
        create: 'Crea pool',
        createGuard: 'I pool esistono solo su un cluster deployato',
        notObserved: 'Nessuna osservazione dei pool — lo sweep del mon riporta qui',
        empty: 'Nessun pool sul cluster',
        colName: 'Nome',
        colReplication: 'Size / min size',
        colPgNum: 'PG',
        colApplication: 'Applicazione',
        colRule: 'Regola CRUSH',
        colUsage: 'Utilizzo',
        observedAtLabel: 'Osservato il',
        objects: '{n} oggetti',
        inUseBy: 'in uso da {name}',
        internalGuard: 'Pool interno di Ceph — la piattaforma non lo elimina mai',
        delete: 'Elimina pool',
        deleteGuard: 'Consumato dallo storage {name} — elimina prima lo storage',
        hint: 'Solo pool replicati (erasure coding fuori scope v1). La replica vive in Ceph: questa tabella è la verità osservata dallo sweep del mon.',
        createTitle: 'Crea pool',
        createHint: 'Il pool nasce sul cluster con la replica richiesta e l’applicazione rbd abilitata — il lavoro lo fa un task su un nodo MON. Accendi "Proponi come storage" per consumarlo subito nello stesso gesto.',
        name: 'Nome del pool',
        nameHint: 'DNS-safe (minuscole, cifre, trattini).',
        size: 'Repliche (size)',
        sizeHint: 'Copie di ogni oggetto. Default 3.',
        minSize: 'Repliche minime (min_size)',
        minSizeHint: 'Copie minime per servire I/O — segue size − 1 finché non lo modifichi.',
        createError: 'La creazione del pool è stata rifiutata',
        deleteTitle: 'Elimina pool',
        deleteBody: 'Il pool {pool} e OGNI oggetto al suo interno vengono distrutti irreversibilmente. Digita il nome del pool per confermare.',
        confirmLabel: 'Nome del pool',
        confirmHint: 'Digita il nome del pool per confermare',
        deleteError: 'L’eliminazione del pool è stata rifiutata',
        detailTitle: 'Dettagli pool',
        autoscaler: 'PG Autoscaler',
        autoscalerOn: 'Attiva',
        autoscalerOff: 'Non attiva',
        autoscalerWarn: 'Notifica',
        pgNumLabel: 'Numero di PG',
        pgNumCaption: 'Osservato: l’autoscaler può cambiarlo in autonomia — questo campo scrive un desiderio.',
        storage: 'Storage TVirt associato',
        addAsStorage: 'Aggiungi come storage TVirt',
        ruleSudoHint: 'Il cambio della regola CRUSH richiede la password operatore.',
        updateError: 'La modifica del pool è stata rifiutata',
        updated: 'Modifica del pool avviata — l’osservazione si aggiorna da sola'
      },
      crush: {
        section: 'Regole CRUSH',
        notObserved: 'Nessuna osservazione CRUSH ancora — lo sweep del mon riporta qui',
        empty: 'Nessuna regola osservata sul cluster',
        hint: 'Una regola decide DOVE finiscono le copie di ogni oggetto — il suo dominio di guasto è l’unità che può guastarsi senza perdere dati. I pool puntano a una regola; la replicated_rule builtin (una replica per server) è il default di nascita.',
        create: 'Crea regola',
        createGuard: 'Le regole CRUSH esistono solo su un cluster deployato',
        createTitle: 'Crea regola CRUSH',
        createHint: 'Una regola = una scelta di dominio di guasto. La regola nasce replicata sul root di default — il lavoro lo fa un task su un nodo MON; associa i pool dal tab Pool dopo.',
        createError: 'La creazione della regola è stata rifiutata',
        name: 'Nome della regola',
        nameHint: 'DNS-safe (minuscole, cifre, trattini).',
        domain: 'Dominio di guasto',
        domainChoice: {
          osd: 'Ogni replica su un OSD diverso',
          host: 'Ogni replica su un server diverso',
          rack: 'Ogni replica in un rack diverso'
        },
        domainCustom: 'Personalizzata (step scritti a mano)',
        domainOther: 'Ogni replica in un {type} diverso',
        rackGuardHint: 'Nessun bucket rack esiste nella gerarchia osservata: la regola si può creare comunque, ma nessun pool potrà puntarla finché un rack non è dichiarato nel posizionamento dei server membri (impostazioni del nodo) — il task di riallineamento costruisce il bucket e l’associazione si apre da sola.',
        treeTitle: 'Gerarchia osservata',
        treeNotObserved: 'Nessuna osservazione della gerarchia — lo sweep del mon riporta qui',
        treeOsds: '{n} OSD',
        treeHint: 'La mappa CRUSH come la vede il cluster: root → livelli dichiarati → server (host) → OSD. La topologia segue il posizionamento dichiarato di ogni server (impostazioni del nodo) — un cambio di posizione la riallinea via task.',
        unplacedWarning: 'Membri senza posizione dichiarata: {nodes} — i loro host restano direttamente sotto la root, fuori dalla topologia dichiarata. Dichiara la posizione nelle impostazioni del nodo (un avviso, mai un blocco).',
        colName: 'Nome',
        colDomain: 'Dominio di guasto',
        colPools: 'Pool',
        builtinChip: 'builtin',
        builtinGuard: 'La regola di default del cluster — la piattaforma non la elimina mai',
        detailAction: 'Dettaglio regola',
        detailType: 'Tipo',
        backToRules: 'Regole',
        ruleNotObserved: 'La lista osservata delle regole non nomina questa regola',
        noPools: 'Nessun pool cavalca questa regola',
        delete: 'Elimina regola',
        deleteTitle: 'Elimina regola CRUSH',
        deleteBody: 'La regola {rule} viene rimossa dalla crushmap del cluster. Nessun pool deve cavalcarla. Digita il nome della regola per confermare.',
        deleteGuard: 'Cavalcata dal pool {name} — ripunta prima il pool',
        confirmLabel: 'Nome della regola',
        confirmHint: 'Digita il nome della regola per confermare',
        deleteError: 'L’eliminazione della regola è stata rifiutata',
        changeRule: 'Cambia regola CRUSH',
        changeRuleTitle: 'Cambia regola CRUSH',
        changeRuleHint: 'Il pool inizia a piazzare i suoi oggetti secondo la regola scelta — Ceph muove i dati esistenti per conformarsi (su un pool pieno segue un ribilanciamento).',
        changeRuleError: 'Il cambio di regola è stato rifiutato',
        rule: 'Regola CRUSH',
        rawTitle: 'Crushmap grezza (decompilata)',
        rawWarning: 'PERICOLO: questo testo è l’INTERA politica di posizionamento del cluster. Una mappa sbagliata può rompere il placement di tutti i pool insieme. Il salvataggio compila e collauda la mappa con crushtool PRIMA di iniettarla — ma una mappa valida può comunque essere una cattiva idea.',
        rawReload: 'Ricarica',
        rawLoading: 'Query in volo — un task read-only sta decompilando la crushmap…',
        rawSave: 'Salva crushmap',
        rawSaveTitle: 'Inietta crushmap',
        rawSaveBody: 'Il testo modificato sostituisce la crushmap dell’INTERO cluster dopo compile + test di crushtool. Digita il nome del cluster per confermare.',
        rawConfirmLabel: 'Nome del cluster',
        rawConfirmHint: 'Digita {name} per confermare',
        rawError: 'L’operazione sulla crushmap è fallita',
        rawHint: 'Il testo è la forma crushtool -d. Errori di compilazione e di test tornano parola per parola da crushtool — la mappa non raggiunge il cluster se uno dei due cancelli fallisce.'
      },
      objects: {
        poolsSection: 'Esploratore oggetti',
        poolsHint: 'Scegli un pool per elencarne le immagini RBD. Ogni lettura profonda corre come task read-only su un nodo MON — non blocca (né è bloccata da) le operazioni reali.',
        deployedGuard: 'Le query sugli oggetti rispondono solo su un cluster deployato',
        browse: 'Sfoglia gli oggetti',
        detailAction: 'Dettaglio immagine',
        backToPools: 'Pool',
        backToImages: 'Immagini',
        refresh: 'Aggiorna',
        loading: 'Query in volo — un task read-only sta interrogando il cluster…',
        error: 'La query è fallita',
        retry: 'Riprova',
        notRbd: 'Pool con application "{app}" — non contiene immagini RBD, niente da esplorare qui',
        noImages: 'Nessuna immagine RBD in questo pool',
        colImage: 'Immagine',
        colSize: 'Dimensione',
        colObjects: 'Oggetti',
        colSnapshots: 'Snapshot',
        imagesHint: 'I dischi su uno storage rbd consumante sono immagini col nome del loro id disco. Il conteggio oggetti è il provisioned (dimensione / dimensione oggetto) — l’uso reale vive nel dettaglio immagine.',
        infoTitle: 'Immagine',
        infoSize: 'Dimensione virtuale',
        infoObjects: 'Oggetti (provisioned)',
        infoObjectSize: 'Dimensione oggetto',
        infoPrefix: 'Prefisso oggetti',
        infoFormat: 'Formato',
        infoFeatures: 'Feature',
        usageTitle: 'Uso reale (rbd du)',
        usageOf: 'di {provisioned} GiB provisioned',
        usageHint: 'Thin per natura: solo gli oggetti scritti occupano byte.',
        watchersTitle: 'Watcher',
        noWatchers: 'Nessun watcher — nessuno ha questa immagine aperta ora',
        snapshotsTitle: 'Snapshot RBD',
        noSnapshots: 'Nessuno snapshot RBD su questa immagine',
        colSnapshot: 'Snapshot',
        colProtected: 'Protetto',
        colTimestamp: 'Creato',
        protected: 'protetto',
        mappingTitle: 'Mappatura fisica (insieme OSD)',
        mappingSampled: 'Campione di {sampled} oggetti su {total} — riassunto onesto: per immagini grandi l’insieme converge a TUTTI gli OSD del pool (CRUSH distribuisce gli oggetti su tutto il pool).',
        mappingComplete: 'Tutti gli oggetti mappati ({total} su {total}).',
        objectQueryTitle: 'Mappa un singolo oggetto',
        objectName: 'Nome oggetto',
        objectHint: 'I nomi oggetto RADOS sono <prefisso>.<indice 16-hex> — ceph osd map risponde con PG e terne OSD.',
        mapObject: 'Mappa',
        primary: 'primario: osd.{osd}'
      },
      registerDevice: {
        title: 'Registra dispositivo OSD',
        hint: 'Su un cluster vivo la registrazione PREPARA anche il dispositivo: diventa un OSD BlueStore con un task automatico affine al nodo (mai vuoto, ratificato 2026-07-24). Un dispositivo appartiene al più a un cluster Ceph in tutta la flotta.',
        disk: 'Disco dati',
        diskHint: 'I dischi dati dichiarati del nodo — nome e seriale',
        noDeclared: 'Nessun disco dati dichiarato su questo nodo — contrassegnali prima nelle impostazioni locali del nodo',
        serialHint: 'Il seriale del dispositivo — il pin di identità',
        encrypted: 'Cripta questo OSD (dmcrypt)',
        encryptedHint: 'La scelta per-OSD (la modalità del cluster è "scelta per ogni OSD"): si congela su questo disco alla registrazione — un cambio di modalità successivo non la riscrive mai; lo fa l’azione di riga cripta/decripta.',
        error: 'La registrazione è stata rifiutata'
      },
      unregisterDevice: {
        title: 'Deregistra dispositivo',
        body: 'Solo registro: {name} smette di essere un candidato OSD di questo cluster. I suoi byte non vengono toccati.',
        error: 'La deregistrazione è stata rifiutata'
      },
      deploy: {
        action: 'Riprova deploy',
        title: 'Riprova il bootstrap',
        body: 'Il bootstrap di nascita riparte dalla configurazione attuale: i mon si avviano, ogni dispositivo registrato viene preparato e attivato come OSD BlueStore. L’operazione corre come macrotask — la fase si aggiorna da sola.',
        started: 'Bootstrap avviato — prosegue come macrotask; la fase si aggiorna da sola',
        error: 'Il bootstrap è stato rifiutato'
      },
      propose: {
        action: 'Proponi come storage',
        title: 'Proponi come storage',
        hintPool: 'Il pool esistente {name} diventa uno storage TVirt: una identità cephx limitata al pool finisce nel keystore e lo storage ceph-rbd nasce pronto. La proposta consuma il pool — non ne crea mai.',
        hintFs: 'Il filesystem esistente {name} diventa uno storage TVirt: una identità cephx limitata al filesystem finisce nel keystore e lo storage cephfs nasce pronto e montato. La proposta consuma il filesystem — non ne crea mai.',
        name: 'Nome dello storage',
        nameHint: 'DNS-1123, unico nello scope — l’identità cephx prende il nome da lui.',
        path: 'Path dentro il filesystem',
        pathHint: 'Path assoluto dentro il filesystem che lo storage monta — / se in dubbio.',
        alsoPropose: 'Proponi subito come storage',
        alsoProposePoolHint: 'Dopo la creazione lo stesso gesto propone il pool appena nato come storage ceph-rbd — due chiamate esplicite, un solo dialog.',
        alsoProposeFsHint: 'Dopo la creazione lo stesso gesto propone il filesystem appena nato come storage cephfs — due chiamate esplicite, un solo dialog.',
        guard: 'Proponi come storage — solo su un cluster healthy',
        started: 'Proposta accettata — lo storage si sta materializzando; lo trovi sotto Storage',
        submit: 'Crea storage',
        error: 'La proposta è stata rifiutata'
      },
      fs: {
        section: 'Filesystem',
        create: 'Crea filesystem',
        createGuard: 'I filesystem esistono solo su un cluster deployato',
        mdsFirstGuard: 'Aggiungi prima un MDS — un filesystem senza metadata server nasce inservibile',
        notObserved: 'Nessuna osservazione dei filesystem — lo sweep del MON riporta qui',
        empty: 'Nessun filesystem sul cluster',
        colName: 'Nome',
        colPools: 'Pool',
        colMds: 'MDS',
        noActiveMds: 'nessun MDS attivo',
        delete: 'Rimuovi filesystem',
        deleteGuard: 'Consumato dallo storage {name} — elimina prima lo storage',
        hint: 'Un filesystem possiede i suoi pool dati e metadata (cephfs.<nome>.data / .meta): nascono con lui e muoiono con lui — removeFs, mai un delete di pool a mano. Questa tabella è la verità osservata dallo sweep del MON.',
        createTitle: 'Crea filesystem',
        createHint: 'Il filesystem nasce sul cluster con i suoi due pool (application cephfs) ed è servito dagli MDS dichiarati — il lavoro lo fa un task su un nodo MON. Accendi "Proponi come storage" per consumarlo subito nello stesso gesto.',
        name: 'Nome del filesystem',
        nameHint: 'DNS-safe (minuscole, cifre, trattini).',
        sizeHint: 'Repliche di ENTRAMBI i pool nascenti. Default 3.',
        createError: 'La creazione del filesystem è stata rifiutata',
        deleteTitle: 'Rimuovi filesystem',
        deleteBody: 'Il filesystem {fs} e OGNI file al suo interno vengono distrutti irreversibilmente — i suoi pool dati e metadata muoiono con lui. Digita il nome del filesystem per confermare.',
        confirmLabel: 'Nome del filesystem',
        confirmHint: 'Digita il nome del filesystem per confermare',
        deleteError: 'La rimozione del filesystem è stata rifiutata'
      },
      destroy: {
        action: 'Distruggi',
        retryAction: "Riprova l'eliminazione",
        retryGuard: "La distruzione sta correndo — il retry vale per una distruzione fallita (il lease rifiuta altrimenti)",
        title: 'Distruggi il cluster',
        body: 'Il fabric deployato viene smantellato: ogni daemon di questo cluster si ferma, il suo stato di sistema e le chiavi nel keystore vengono rilasciati. A distruzione riuscita il cluster viene rimosso automaticamente.',
        confirmLabel: 'Nome del cluster',
        confirmHint: 'Digita il nome del cluster per confermare',
        wipe: 'Cancella i dispositivi registrati',
        wipeHint: 'Spuntata: le firme di ogni dispositivo registrato vengono cancellate — i dati sopra sono distrutti. Non spuntata: i byte restano intatti e i dispositivi tornano registrati.',
        guard: 'Distruggi — nulla di deployato',
        started: 'Distruzione avviata — prosegue come macrotask; la fase si aggiorna da sola',
        error: 'La distruzione è stata rifiutata'
      },
      delete: {
        guard: 'Elimina — solo una configurazione non deployata (prima distruggi)',
        title: 'Eliminare {name}?',
        body: 'La configurazione e il suo registro dispositivi vengono rimossi. Legale solo finché nulla è deployato.',
        error: "L'eliminazione è stata rifiutata"
      }
    }
  }
}
